import { useCopilotAction } from '@copilotkit/react-core';

import { Camera } from 'types/camera';

import { useAPI } from 'hooks/api/useAPI';

// Helper to safely parse JSON or return the value if already parsed
function safeJsonParse<T>(value: string | T | undefined): T | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string') return value as T;
  try {
    return JSON.parse(value) as T;
  } catch {
    // If it's a string that's not valid JSON, treat it as a single-item array
    return [value] as unknown as T;
  }
}

export function useListCamerasTool() {
  const { applicationID, client } = useAPI();

  useCopilotAction({
    name: 'listCameras',
    description:
      'List cameras in the system. Use this to find camera IDs for filtering analytics or file searches. Can filter by status, name, model, or gateway.',
    parameters: [
      {
        name: 'ids',
        type: 'string',
        description: 'JSON array of camera IDs to fetch',
        required: false,
      },
      {
        name: 'statuses',
        type: 'string',
        description:
          'JSON array of statuses to filter by: ["online", "offline", "unknown", "unauthorized"]',
        required: false,
      },
      {
        name: 'names',
        type: 'string',
        description: 'JSON array of camera names to search for',
        required: false,
      },
      {
        name: 'gatewayIds',
        type: 'string',
        description: 'JSON array of gateway IDs to filter cameras by',
        required: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return. Defaults to 50.',
        required: false,
      },
    ],
    handler: async (args: {
      ids?: string | string[];
      statuses?: string | string[];
      names?: string | string[];
      gatewayIds?: string | string[];
      limit?: number;
    }) => {
      try {
        const ids = safeJsonParse<string[]>(args.ids);
        const statuses = safeJsonParse<string[]>(args.statuses);
        const names = safeJsonParse<string[]>(args.names);
        const gatewayIds = safeJsonParse<string[]>(args.gatewayIds);

        const { data } = await client.get(`/apps/${applicationID}/cameras`, {
          params: {
            pagination: 'offset',
            page: 1,
            limit: args.limit || 50,
            camera_ids: ids,
            statuses,
            camera_names: names,
            gateway_ids: gatewayIds,
          },
        });

        const cameras = data?.data || [];

        if (cameras.length === 0) {
          return {
            message: 'No cameras found matching your criteria.',
            cameras: [],
          };
        }

        return {
          message: `Found ${cameras.length} camera(s)`,
          cameras: cameras.map((camera: Camera) => camera),
        };
      } catch (error) {
        return {
          message: 'Failed to load cameras',
          error: error instanceof Error ? error.message : 'Unknown error',
          cameras: [],
        };
      }
    },
  });
}
